import type { Senario, SpilloverPriority } from 'src/entities/planningEntity';
import type { HowToPlanning } from 'src/entities/PlanningHistory.entity';
import type { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

export type DispatchModel = 'standard' | 'shiki' | 'sahai';
export type VehicleSizePreference = 'none' | 'small' | 'large';

export const DISPATCH_MODEL_DEFS: readonly { value: DispatchModel; label: string; description: string }[] = [
  { value: 'standard', label: 'LOG - 3.0', description: '標準配車モデル' },
  { value: 'shiki', label: 'LOG - 3.0 SHIKI', description: '学習配車モデル' },
  { value: 'sahai', label: 'LOG - 3.0 SAHAI', description: '超過分の差配モデル' },
];

/** 自動配車ダイアログの設定。無効にする制約は親画面の state なので含めない */
export type PlanningDialogSettings = {
  dispatchModel: DispatchModel;
  howToPlanning: HowToPlanning;
  vehicleSizePreference: VehicleSizePreference;
  availabilityOfExpressway: boolean;
  includeFutureDeliveries: boolean;
  dontExchangePreroute: boolean;
  activateIncrementalMaxNumPistons: boolean;
  autoSplitOrders: boolean;
  concurrentAllOrNothing: boolean;
  expandWorkingTimeStartMinutes: number;
  expandWorkingTimeEndMinutes: number;
  expandLoadStartMinutes: number;
  expandLoadEndMinutes: number;
  expandUnloadStartMinutes: number;
  expandUnloadEndMinutes: number;
  expandMaxVolumeRate: number;
  expandMaxLoadCapacityRate: number;
};

// How: 初期値・「初期化」・非表示項目の送信値をこの 1 つにまとめる。
export const DEFAULT_PLANNING_SETTINGS: PlanningDialogSettings = {
  dispatchModel: 'standard',
  howToPlanning: '最適化',
  vehicleSizePreference: 'none',
  availabilityOfExpressway: true,
  includeFutureDeliveries: true,
  dontExchangePreroute: true,
  activateIncrementalMaxNumPistons: false,
  autoSplitOrders: false,
  concurrentAllOrNothing: false,
  expandWorkingTimeStartMinutes: 0,
  expandWorkingTimeEndMinutes: 0,
  expandLoadStartMinutes: 0,
  expandLoadEndMinutes: 0,
  expandUnloadStartMinutes: 0,
  expandUnloadEndMinutes: 0,
  expandMaxVolumeRate: 0,
  expandMaxLoadCapacityRate: 0,
};

/** LOG 3.0 でだけ画面に出す項目。SHIKI / SAHAI では既定値を送る */
export const STANDARD_ONLY_SETTING_KEYS = [
  'howToPlanning',
  'vehicleSizePreference',
  'includeFutureDeliveries',
  'dontExchangePreroute',
  'activateIncrementalMaxNumPistons',
  'autoSplitOrders',
  'concurrentAllOrNothing',
] as const satisfies readonly (keyof PlanningDialogSettings)[];

/** 制約緩和の 8 項目。SAHAI では 0 を送る */
export const EXPAND_SETTING_KEYS = [
  'expandWorkingTimeStartMinutes',
  'expandWorkingTimeEndMinutes',
  'expandLoadStartMinutes',
  'expandLoadEndMinutes',
  'expandUnloadStartMinutes',
  'expandUnloadEndMinutes',
  'expandMaxVolumeRate',
  'expandMaxLoadCapacityRate',
] as const satisfies readonly (keyof PlanningDialogSettings)[];

const pickDefaults = <K extends keyof PlanningDialogSettings>(keys: readonly K[]) =>
  Object.fromEntries(keys.map((key) => [key, DEFAULT_PLANNING_SETTINGS[key]])) as Pick<PlanningDialogSettings, K>;

/**
 * 保存値のモデルを今の内部 ID に読み替える。
 * #286 では localStorage 互換のため value(opus/repro/fusion) を据え置いたが、#366 で
 * 配車シナリオを SHIKI / SAHAI に昇格させたので、ここで旧値を読み替えて ID を改めた。
 */
export function migrateDispatchModel(model: unknown, scenario: unknown): DispatchModel {
  switch (model) {
    case 'standard':
    case 'shiki':
    case 'sahai':
      return model;
    case 'fusion':
      return scenario === 'spillover_duration' || scenario === 'spillover_trucks' ? 'sahai' : 'shiki';
    default:
      // opus(=標準)・repro(=Trace。#366 で廃止)・未保存・不明な値
      return 'standard';
  }
}

export type AlgorithmSelection = {
  senario: Senario;
  spilloverPriority: SpilloverPriority | undefined;
  traceCompanyId: number;
  disableGlobalOpt: boolean;
};

export function toAlgorithmSelection(model: DispatchModel, companyId: number): AlgorithmSelection {
  switch (model) {
    case 'shiki':
      return { senario: 'apprentice', spilloverPriority: undefined, traceCompanyId: companyId, disableGlobalOpt: false };
    case 'sahai':
      // API も trucks に固定する(#366)が、未指定だとアルゴリズムが duration に落ちるので FE からも明示する
      return { senario: 'spillover', spilloverPriority: 'trucks', traceCompanyId: 0, disableGlobalOpt: false };
    default:
      return { senario: 'setcover', spilloverPriority: undefined, traceCompanyId: 0, disableGlobalOpt: false };
  }
}

export type EffectivePlanningSettings = {
  settings: PlanningDialogSettings;
  selectedRelaxedRules: RelaxedRuleVo[];
};

/**
 * 実際に送る値を返す。model にはライセンス込みの有効なモデルを渡す。
 * Why not 画面の state を既定値で書き換えない: LOG 3.0 に戻したとき前回の値を復元するため(#366 D1)。
 *   見えない保存値をそのまま送ると、見えない設定で配車結果が変わる(#292 と同型)ので送る値だけ差し替える。
 */
export function resolveEffectiveSettings(
  model: DispatchModel,
  settings: PlanningDialogSettings,
  selectedRelaxedRules: RelaxedRuleVo[],
): EffectivePlanningSettings {
  if (model === 'standard') {
    return { settings: { ...settings, dispatchModel: model }, selectedRelaxedRules };
  }

  const hidden = { ...settings, ...pickDefaults(STANDARD_ONLY_SETTING_KEYS), dispatchModel: model };
  if (model === 'shiki') {
    return { settings: hidden, selectedRelaxedRules };
  }

  // SAHAI は制約 UI を出さない。state のリセット(PlanningPresenter)は描画後の effect なので、
  // ライセンス取得や復元と重なった 1 コミットでも制約を送らないよう、ここでも外す
  return { settings: { ...hidden, ...pickDefaults(EXPAND_SETTING_KEYS) }, selectedRelaxedRules: [] };
}
